<script lang="ts" setup>
import { ref, onMounted, watch, nextTick, onUnmounted, computed, useTemplateRef } from 'vue';
import { useDebounceFn } from '@vueuse/core';

interface Props {
  speed?: number; // 滚动速度（像素/秒）
  pauseAtStart?: number; // 起始位置停留时间（秒）
  pauseAtEnd?: number; // 结束位置停留时间（秒）
  pauseOnReturn?: number; // 返程前停留时间（秒）
  autoStart?: boolean; // 是否自动开始滚动
  pauseOnHover?: boolean; // 鼠标悬停时是否暂停
  direction?: 'horizontal' | 'vertical'; // 滚动方向类型：水平或垂直
  reverse?: boolean; // 是否反向滚动
}

const props = withDefaults(defineProps<Props>(), {
  speed: 50,
  pauseAtStart: 1,
  pauseAtEnd: 1,
  pauseOnReturn: 0.5,
  autoStart: true,
  pauseOnHover: true,
  direction: 'horizontal',
  reverse: false,
})

const containerRef = useTemplateRef('containerEl')
const textRef = useTemplateRef('textEl')
const isOverflowing = ref(false)
const isAnimating = ref(false)
const isPaused = ref(false)
const scrollDistance = ref(0)
const animationId = ref(0)

let cachedStyleElement: HTMLStyleElement | null = null

const animationParams = computed(() => {
  if (!textRef.value || scrollDistance.value === 0) {
    return null;
  }

  const distance = scrollDistance.value

  // 单程滚动时间
  const scrollTime = distance / props.speed
  const totalTime
    = props.pauseAtStart
      + scrollTime
      + props.pauseAtEnd
      + props.pauseOnReturn
      + scrollTime

  // 计算各阶段的时间百分比
  const pauseStartPercent = (props.pauseAtStart / totalTime) * 100
  const reachEndPercent = ((props.pauseAtStart + scrollTime) / totalTime) * 100
  const pauseEndPercent = ((props.pauseAtStart + scrollTime + props.pauseAtEnd) / totalTime) * 100
  const pauseReturnPercent = ((props.pauseAtStart + scrollTime + props.pauseAtEnd + props.pauseOnReturn) / totalTime) * 100

  return {
    totalTime,
    distance,
    pauseStartPercent,
    reachEndPercent,
    pauseEndPercent,
    pauseReturnPercent,
  }
})

const generateKeyframes = () => {
  const params = animationParams.value

  if (!params) {
    return '';
  }

  const isHorizontal = props.direction === 'horizontal'
  const isReverse = props.reverse

  let transformStart: string
  let transformEnd: string

  if (isHorizontal) {
    transformStart = isReverse ? `translateX(-${params.distance}px)` : 'translateX(0)'
    transformEnd = isReverse ? 'translateX(0)' : `translateX(-${params.distance}px)`
  }
  else {
    transformStart = isReverse ? `translateY(-${params.distance}px)` : 'translateY(0)'
    transformEnd = isReverse ? 'translateY(0)' : `translateY(-${params.distance}px)`
  }

  return `@keyframes scroll-uniform-${animationId.value} {
    0% { transform: ${transformStart}; }
    ${params.pauseStartPercent}% { transform: ${transformStart}; }
    ${params.reachEndPercent}% { transform: ${transformEnd}; }
    ${params.pauseEndPercent}% { transform: ${transformEnd}; }
    ${params.pauseReturnPercent}% { transform: ${transformEnd}; }
    100% { transform: ${transformStart}; }
  }`
}

const checkOverflow = async (forceAsync = false) => {
  if (forceAsync) {
    await nextTick()
  }

  if (!containerRef.value || !textRef.value) {
    return;
  }

  const isHorizontal = props.direction === 'horizontal'

  let containerSize: number
  let textSize: number

  if (isHorizontal) {
    containerSize = containerRef.value.clientWidth
    textSize = textRef.value.scrollWidth
  }
  else {
    containerSize = containerRef.value.clientHeight
    textSize = textRef.value.scrollHeight
  }

  const wasOverflowing = isOverflowing.value
  isOverflowing.value = textSize > containerSize

  if (isOverflowing.value) {
    scrollDistance.value = textSize - containerSize
  }

  return wasOverflowing !== isOverflowing.value
}

const createOrUpdateStyleElement = () => {
  const styleId = `text-overflow-keyframes-${animationId.value}`

  // 清理旧的样式元素
  if (cachedStyleElement && cachedStyleElement.id !== styleId) {
    cachedStyleElement.remove()
    cachedStyleElement = null
  }

  if (!cachedStyleElement) {
    cachedStyleElement = document.createElement('style')
    cachedStyleElement.id = styleId
    document.head.appendChild(cachedStyleElement)
  }

  cachedStyleElement.textContent = generateKeyframes()
  return styleId
}

// 开始滚动动画
const startAnimation = () => {
  if (!isOverflowing.value || isAnimating.value) {
    return;
  }

  isAnimating.value = true

  if (!textRef.value) {
    return;
  }

  const params = animationParams.value
  if (!params) {
    return;
  }

  // 生成更唯一动画ID
  animationId.value = Date.now() + Math.floor(Math.random() * 1000)

  createOrUpdateStyleElement()

  const textElement = textRef.value
  textElement.style.cssText += `
    --animation-name: scroll-uniform-${animationId.value};
    --total-duration: ${params.totalTime}s;
  `

  // 添加动画类
  textElement.classList.add('scrolling-uniform')
}

// 停止滚动动画
const stopAnimation = () => {
  isAnimating.value = false
  if (textRef.value) {
    textRef.value.classList.remove('scrolling-uniform')
  }
}

// 暂停/恢复动画
const pauseAnimation = () => {
  isPaused.value = true
  if (isOverflowing.value && textRef.value) {
    textRef.value.style.animationPlayState = 'paused'
  }
}

const resumeAnimation = () => {
  isPaused.value = false
  if (isOverflowing.value && textRef.value) {
    textRef.value.style.animationPlayState = 'running'
  }
}

// 处理鼠标悬停
const handleMouseEnter = () => {
  if (props.pauseOnHover) {
    pauseAnimation()
  }
}

const handleMouseLeave = () => {
  if (props.pauseOnHover) {
    resumeAnimation()
  }
}

const restartAnimationDelayed = (delay = 100) => {
  stopAnimation()
  setTimeout(() => {
    if (props.autoStart && isOverflowing.value) {
      startAnimation()
    }
  }, delay)
}

// 监听速度相关参数变化
watch([
  () => props.speed,
  () => props.pauseAtStart,
  () => props.pauseAtEnd,
  () => props.pauseOnReturn,
], () => {
  if (isAnimating.value) {
    restartAnimationDelayed()
  }
})

const handleResize = useDebounceFn(() => {
  checkOverflow().then((stateChanged) => {
    if (stateChanged) {
      restartAnimationDelayed()
    }
  })
}, 150)

onMounted(async () => {
  await checkOverflow(true)

  if (props.autoStart && isOverflowing.value) {
    // 等待内容稳定后启动动画
    const startAnimationWhenStable = async () => {
      const initialDistance = scrollDistance.value

      // 等待字体加载（如果支持）
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready
      }

      // 检查内容是否稳定
      await nextTick()
      await checkOverflow()

      // 如果内容变化大，再等待一次
      if (Math.abs(scrollDistance.value - initialDistance) > 2) {
        await new Promise(resolve => setTimeout(resolve, 100))
        await checkOverflow()
      }

      if (isOverflowing.value) {
        startAnimation()
      }
    }

    // 使用更短的初始延迟，动态调整
    setTimeout(startAnimationWhenStable, 100)
  }

  window.addEventListener('resize', handleResize, { passive: true })
})

onUnmounted(() => {
  stopAnimation()

  // 清理样式元素
  if (cachedStyleElement) {
    cachedStyleElement.remove()
    cachedStyleElement = null
  }

  // 清理事件监听器
  window.removeEventListener('resize', handleResize)
})

defineExpose({
  startAnimation,
  stopAnimation,
  pauseAnimation,
  resumeAnimation,
  checkOverflow,
})
</script>

<template>
  <div
    ref="containerEl"
    class="alternate-marquee relative w-full overflow-hidden"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div
      ref="textEl"
      class="whitespace-nowrap will-change-transform"
      :class="{
        'transform-none': !isOverflowing,
        'translate-0': isOverflowing,
        'w-full whitespace-normal': direction === 'vertical'
      }"
    >
      <slot></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.scrolling-uniform {
  animation: var(--animation-name) var(--total-duration) linear infinite;
}

// 减少动画偏好的用户
@media (prefers-reduced-motion: reduce) {
  .scrolling-uniform {
    animation: none;
  }

  .alternate-marquee {
    &::after {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 30px;
      background: linear-gradient(to left, currentColor, transparent);
      opacity: 0.1;
      content: "";
      pointer-events: none;
    }
  }
}
</style>
